import { BadgeColor, BadgeShape, ExBadge } from '@boomi/exosphere';
import { useCallback, useEffect, useState } from 'react';

import {
    getServiceInvokerRequest,
    getServiceInvokerRequestsByState,
} from '../../../sources/serviceInvoker';
import translations from '../../../translations';
import type { ServiceInvokerRequestAPI } from '../../../types/service';
import type { StateResponseAPI } from '../../../types/states';
import { stringReplace } from '../../../utils/string';
import SyntaxHighlighter from '../../generic/SyntaxHighlighter';
import Table, { type TableColumnList } from '../../generic/Table';
import GenericModal from '../../generic/modal/GenericModal';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { type SelectedFlow, View } from '../InsightsDashboard';
import RootFaultsInsights from './RootFaultsInsights';
import { formatJSONContent } from '../../../utils/json';

const renderStatus = (attributes: Record<string, unknown>) => {
    if ((attributes['numberOfFailures'] as number) > 0) {
        return (
            <ExBadge shape={BadgeShape.ROUND} color={BadgeColor.RED}>
                {translations.DASHBOARD_failed}
            </ExBadge>
        );
    }
    return (
        <ExBadge shape={BadgeShape.ROUND} color={BadgeColor.GREEN}>
            {translations.DASHBOARD_ok}
        </ExBadge>
    );
};

interface Props {
    selectedFlow: SelectedFlow;
    setSelectedFlow: (selectedFlow: SelectedFlow) => void;
    stateDetails: StateResponseAPI | null;
    setStateDetails: (stateDetails: StateResponseAPI | null) => void;
    setCurrentView: (view: View) => void;
    screen?: string;
}

const ConnectorInvokerRequestInsights = ({
    selectedFlow,
    setSelectedFlow,
    stateDetails,
    setStateDetails,
    setCurrentView,
}: Props) => {
    const [requests, setRequests] = useState<ServiceInvokerRequestAPI[]>([]);
    const [content, setContent] = useState<string>('');
    const [showContent, setShowContent] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const dispatchNotification = useNotificationDispatch();

    // Filters and pagination
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [total, setTotal] = useState(0);

    const refreshRequests = useCallback(
        (page = 1) => {
            if (!stateDetails?.id) {
                return;
            }

            setPage(page);
            setIsLoading(true);

            getServiceInvokerRequestsByState({
                stateId: stateDetails.id,
                page,
                pageSize,
            })
                .then((response) => {
                    setRequests(response.items);
                    setTotal(response._meta.total);
                })
                .catch((error) => {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: { error },
                    });
                })
                .finally(() => setIsLoading(false));
        },
        [dispatchNotification, pageSize, stateDetails?.id],
    );

    const onShowFailures = (serviceInvokerRequest: ServiceInvokerRequestAPI) => {
        getServiceInvokerRequest({
            requestId: serviceInvokerRequest.id,
        })
            .then((response) => {
                const failures = response.failures;
                setContent(failures ? JSON.stringify(failures) : '');
                setShowContent(true);
            })
            .catch((error) => {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        refreshRequests();
    }, [refreshRequests]);

    const columns: TableColumnList<ServiceInvokerRequestAPI> = [
        {
            renderHeader: () => translations.COMMON_TABLE_occurred_at_utc,
            renderCell: ({ item }) =>
                new Date(item.createdAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'medium',
                }),
            size: '11rem',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_uri,
            renderCell: ({ item }) => item.uri,
        },
        {
            renderHeader: () => translations.COMMON_TABLE_method,
            renderCell: ({ item }) => item.method,
            size: '100px',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_status,
            renderCell: ({ item }) => renderStatus(item.attributes),
            size: '100px',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item }) => (
                <>
                    <button
                        className="link-emulate"
                        onClick={() => onShowFailures(item)}
                        type="button"
                    >
                        {translations.DASHBOARD_failures}
                    </button>
                </>
            ),
            size: '10rem',
        },
    ];

    return (
        <>
            <span className="title-bar">
                <h1>
                    <button
                        className="link-emulate"
                        onClick={() => {
                            setCurrentView(View.summary);
                            setStateDetails(null);
                            setSelectedFlow({ id: '', name: '' });
                        }}
                        type="button"
                    >
                        {translations.DASHBOARD_header}
                    </button>
                    {' > '}
                    <button
                        className="link-emulate"
                        onClick={() => {
                            setCurrentView(View.states);
                            setStateDetails(null);
                        }}
                        type="button"
                    >
                        {stringReplace(translations.DASHBOARD_states_header, selectedFlow.name)}
                    </button>
                    {' > '}
                    {translations.DASHBOARD_connector_request_header}
                </h1>
            </span>
            <h2>{translations.DASHBOARD_connect_log_events}</h2>
            <Table
                columns={columns}
                items={requests}
                isLoading={isLoading}
                pagination={{
                    page,
                    total,
                    pageSize,
                    changePage: (nextPage) => refreshRequests(nextPage),
                }}
                rowClassName={() => 'generic-row generic-row-tall'}
            />
            <h2>{translations.DASHBOARD_root_faults_header}</h2>
            {stateDetails && <RootFaultsInsights selectedState={stateDetails} />}
            <GenericModal
                className="metadata-editor"
                show={showContent}
                onHide={() => setShowContent(false)}
                title={translations.DASHBOARD_failures}
                renderBody={() => (
                    <SyntaxHighlighter content={formatJSONContent(content ?? '')} language="json" />
                )}
                renderFooter={() => (
                    <div className="flex">
                        <div className="flex-child-right">
                            <button
                                className="btn btn-sm btn-default"
                                onClick={() => setShowContent(false)}
                                type="button"
                            >
                                {translations.COMMON_close}
                            </button>
                        </div>
                    </div>
                )}
            />
        </>
    );
};

export default ConnectorInvokerRequestInsights;
